import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, MenuItem, Button, CircularProgress, Alert,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Refresh, Search, Visibility, Close, History } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebaseConfig';

interface AuditEntry {
  id: string;
  order_id: string;
  order_number: string;
  action: string;
  user_email: string;
  user_role: string;
  timestamp: string | null;
  changes: Array<{ field: string; old: any; new: any }>;
  previous_status?: string;
  new_status?: string;
  reason?: string;
  manager_pin_used?: boolean;
  metadata?: any;
}

const ACTION_COLORS: Record<string, string> = {
  create: '#4CAF50',
  update: '#2196F3',
  status_change: '#FF9800',
  cancel: '#f44336',
  refund: '#9C27B0',
  discount: '#FFEB3B',
  payment_change: '#00BCD4',
  driver_assignment: '#3F51B5',
  items_change: '#FF5722',
  complete: '#4CAF50',
  delete: '#f44336',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  status_change: 'Status Change',
  cancel: 'Cancelled',
  refund: 'Refunded',
  discount: 'Discount Applied',
  payment_change: 'Payment Changed',
  driver_assignment: 'Driver Assigned',
  items_change: 'Items Changed',
  complete: 'Completed',
  delete: 'Deleted',
};

// Friendly labels for raw Firestore field names — the raw names (e.g.
// rider_email, order_type, last_modified_by) aren't meaningful to a non-technical
// admin. These are what actually render in the "Field Changes" table.
const FIELD_LABELS: Record<string, string> = {
  status: 'Order Status',
  order_type: 'Order Type',
  payment_method: 'Payment Method',
  payment_status: 'Payment Status',
  customer_name: 'Customer Name',
  customer_phone: 'Customer Phone',
  customer_address: 'Delivery Address',
  table_number: 'Table',
  total: 'Total Amount',
  subtotal: 'Subtotal',
  tax: 'VAT',
  items: 'Items',
  item_count: 'Item Count',
  rider_email: 'Driver Email',
  rider_name: 'Driver Name',
  assigned_driver_email: 'Assigned Driver',
  assigned_driver_name: 'Assigned Driver Name',
  notes: 'Notes',
  discount_amount: 'Discount',
  discount_type: 'Discount Type',
  discount_reason: 'Discount Reason',
  refund_amount: 'Refund Amount',
  refund_reason: 'Refund Reason',
  manager_pin_used: 'Manager Approved',
  last_modified_by: 'Last Modified By',
  last_modification_reason: 'Modification Reason',
  delivered_at: 'Delivered At',
  picked_up_at: 'Picked Up At',
  sealed_at: 'Sealed At',
  sealed_by: 'Sealed By',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  assigned: 'Assigned to Driver',
  picked_up: 'Picked Up',
  in_transit: 'In Transit',
  on_delivery: 'On Delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  dine_in: 'Dine-In',
  restaurant: 'Dine-In',
  takeaway: 'Takeaway',
  pickup: 'Pickup',
  delivery: 'Delivery',
};

const friendlyField = (raw: string) => FIELD_LABELS[raw] || raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const friendlyStatus = (raw: string) => STATUS_LABELS[raw] || raw;
const friendlyOrderType = (raw: string) => ORDER_TYPE_LABELS[raw] || raw;
const friendlyValue = (field: string, v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'status') return friendlyStatus(String(v));
  if (field === 'order_type') return friendlyOrderType(String(v));
  if (field === 'total' || field === 'subtotal' || field === 'tax' || field === 'discount_amount' || field === 'refund_amount') {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? String(v) : `€${n.toFixed(2)}`;
  }
  if (field === 'manager_pin_used') return v === true ? 'Yes' : 'No';
  if (field === 'items' && Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterUser, setFilterUser] = useState<string>('');
  const [filterOrder, setFilterOrder] = useState<string>('');
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getAuditLog = httpsCallable(functions, 'getAuditLog');
      const params: any = { limit: 200 };
      if (filterAction) params.action = filterAction;
      if (filterUser) params.user_email = filterUser;
      if (filterOrder) params.order_id = filterOrder;
      const result = await getAuditLog(params);
      const data = result.data as any;
      setEntries(data.entries || []);
    } catch (err: any) {
      console.error('Audit log fetch failed:', err);
      setError(err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterUser, filterOrder]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const formatTime = (ts: string | null) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return '—'; }
  };

  const formatChangeValue = (val: any): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'object') return JSON.stringify(val).substring(0, 50);
    return String(val);
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#e0e0e0', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <History sx={{ color: '#00C896', fontSize: 32 }} />
              Audit Log
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mt: 0.5 }}>
              Complete history of every order modification — for dispute resolution and fraud detection
            </Typography>
          </Box>
          <Button
            variant="contained" startIcon={<Refresh />} onClick={loadEntries} disabled={loading}
            sx={{ background: '#00C896', color: '#1a1d2e', fontWeight: 700, '&:hover': { background: '#7bc012' } }}>
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#1a1d2e', border: '1px solid #2a2d42' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Order ID" size="small" value={filterOrder}
              onChange={(e) => setFilterOrder(e.target.value)}
              placeholder="e.g. abc123..."
              sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' } }, '& .MuiInputLabel-root': { color: '#9ca3af' } }}
              InputProps={{ startAdornment: <Search sx={{ color: '#9ca3af', mr: 1 }} /> }}
            />
            <TextField
              label="User Email" size="small" value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              placeholder="e.g. cashier@..."
              sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' } }, '& .MuiInputLabel-root': { color: '#9ca3af' } }}
            />
            <TextField
              select label="Action Type" size="small" value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' } }, '& .MuiInputLabel-root': { color: '#9ca3af' } }}
              SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1a1d2e', color: '#fff' } } } }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={() => { setFilterAction(''); setFilterUser(''); setFilterOrder(''); }}
              sx={{ borderColor: '#3a3d52', color: '#9ca3af' }}>Clear Filters</Button>
          </Box>
        </Paper>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ px: 2, py: 1, bgcolor: 'rgba(0, 200, 150,0.08)', border: '1px solid rgba(0, 200, 150,0.2)' }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', fontWeight: 600 }}>TOTAL ENTRIES</Typography>
            <Typography sx={{ color: '#00C896', fontSize: '1.4rem', fontWeight: 700 }}>{entries.length}</Typography>
          </Paper>
          {Object.entries(ACTION_LABELS).slice(0, 5).map(([key, label]) => {
            const count = entries.filter(e => e.action === key).length;
            if (count === 0) return null;
            return (
              <Paper key={key} sx={{ px: 2, py: 1, bgcolor: '#1a1d2e', border: '1px solid #2a2d42' }}>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>{label}</Typography>
                <Typography sx={{ color: ACTION_COLORS[key] || '#fff', fontSize: '1.2rem', fontWeight: 700 }}>{count}</Typography>
              </Paper>
            );
          })}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Table */}
        <TableContainer component={Paper} sx={{ bgcolor: '#1a1d2e', border: '1px solid #2a2d42' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Time</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Order #</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Status Change</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Reason</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Changes</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, borderBottom: 'none' }}><CircularProgress sx={{ color: '#00C896' }} /></TableCell></TableRow>
              ) : entries.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: '#6b7280', borderBottom: 'none' }}>No audit log entries found.</TableCell></TableRow>
              ) : entries.map((e) => (
                <TableRow key={e.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 150,0.04)' } }}>
                  <TableCell sx={{ color: '#9ca3af', fontSize: '0.75rem', borderBottom: '1px solid #2a2d42' }}>{formatTime(e.timestamp)}</TableCell>
                  <TableCell sx={{ color: '#00C896', fontSize: '0.78rem', fontWeight: 600, borderBottom: '1px solid #2a2d42' }}>#{e.order_number || '—'}</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #2a2d42' }}>
                    <Chip label={ACTION_LABELS[e.action] || e.action} size="small"
                      sx={{ bgcolor: `${ACTION_COLORS[e.action] || '#666'}22`, color: ACTION_COLORS[e.action] || '#fff', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                    {e.manager_pin_used && (
                      <Chip label="🔒 PIN" size="small" sx={{ ml: 0.5, bgcolor: 'rgba(255,193,7,0.15)', color: '#FFC107', fontWeight: 600, fontSize: '0.6rem', height: 20 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #2a2d42' }}>
                    <Typography sx={{ color: '#e0e0e0', fontSize: '0.78rem', fontWeight: 500 }}>{e.user_email}</Typography>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', textTransform: 'uppercase' }}>{e.user_role || 'unknown'}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>
                    {e.previous_status && e.new_status && e.previous_status !== e.new_status
                      ? <span><span style={{ color: '#666' }}>{friendlyStatus(e.previous_status)}</span> → <span style={{ color: '#00C896', fontWeight: 600 }}>{friendlyStatus(e.new_status)}</span></span>
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#FFC107', fontSize: '0.72rem', fontStyle: 'italic', borderBottom: '1px solid #2a2d42', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={e.reason || ''}>
                    {e.reason ? `"${e.reason}"` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>
                    {e.changes && e.changes.length > 0 ? `${e.changes.length} field${e.changes.length > 1 ? 's' : ''}` : '—'}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #2a2d42' }}>
                    <IconButton size="small" onClick={() => setSelectedEntry(e)} sx={{ color: '#64B5F6' }}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Detail Dialog */}
        <Dialog open={!!selectedEntry} onClose={() => setSelectedEntry(null)} maxWidth="md" fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1d2e', border: '1px solid #2a2d42', borderRadius: 3 } }}>
          {selectedEntry && (
            <>
              <DialogTitle sx={{ borderBottom: '1px solid #2a2d42', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                    Audit Entry — #{selectedEntry.order_number}
                  </Typography>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>{formatTime(selectedEntry.timestamp)}</Typography>
                </Box>
                <IconButton onClick={() => setSelectedEntry(null)} sx={{ color: '#9ca3af' }}><Close /></IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {/* Reason — prominent at top if the action required manager approval.
                    Previously buried at the bottom in a small yellow italic; admins missed it. */}
                {selectedEntry.reason && (
                  <Paper sx={{ p: 2, mb: 2.5, bgcolor: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.3)', borderRadius: 2 }}>
                    <Typography sx={{ color: '#FFC107', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                      🛡 Reason Given
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '0.95rem', fontStyle: 'italic', lineHeight: 1.4 }}>
                      "{selectedEntry.reason}"
                    </Typography>
                    {selectedEntry.manager_pin_used && (
                      <Typography sx={{ color: '#FFC107', fontSize: '0.72rem', mt: 0.8 }}>
                        Approved by a manager (PIN verified)
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Summary card */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>What happened</Typography>
                    <Chip label={ACTION_LABELS[selectedEntry.action] || selectedEntry.action} size="small"
                      sx={{ bgcolor: `${ACTION_COLORS[selectedEntry.action] || '#666'}22`, color: ACTION_COLORS[selectedEntry.action], fontWeight: 700, mt: 0.5 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Who did it</Typography>
                    <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>{selectedEntry.user_email}</Typography>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                      Role: {selectedEntry.user_role || 'unknown'}
                    </Typography>
                  </Box>
                  {selectedEntry.previous_status && selectedEntry.new_status && (
                    <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                      <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Status change</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Chip label={friendlyStatus(selectedEntry.previous_status)} size="small"
                          sx={{ bgcolor: 'rgba(239,83,80,0.15)', color: '#ef5350', fontWeight: 600 }} />
                        <Typography sx={{ color: '#9ca3af' }}>→</Typography>
                        <Chip label={friendlyStatus(selectedEntry.new_status)} size="small"
                          sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#4CAF50', fontWeight: 700 }} />
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* What was changed — human readable table */}
                {selectedEntry.changes && selectedEntry.changes.length > 0 && (
                  <>
                    <Typography sx={{ color: '#00C896', fontWeight: 700, mb: 1, mt: 2, fontSize: '0.9rem' }}>What was changed</Typography>
                    <TableContainer sx={{ border: '1px solid #2a2d42', borderRadius: 1.5 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Field</TableCell>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Before</TableCell>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>After</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedEntry.changes.map((c, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ color: '#e0e0e0', fontSize: '0.82rem', fontWeight: 600, borderBottom: '1px solid #2a2d42' }}>{friendlyField(c.field)}</TableCell>
                              <TableCell sx={{ color: '#ef5350', fontSize: '0.82rem', borderBottom: '1px solid #2a2d42' }}>{friendlyValue(c.field, c.old)}</TableCell>
                              <TableCell sx={{ color: '#4CAF50', fontSize: '0.82rem', fontWeight: 500, borderBottom: '1px solid #2a2d42' }}>{friendlyValue(c.field, c.new)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Order snapshot — replaces the raw JSON "Metadata" pre-block */}
                {selectedEntry.metadata && Object.keys(selectedEntry.metadata).length > 0 && (
                  <>
                    <Typography sx={{ color: '#00C896', fontWeight: 700, mb: 1, mt: 3, fontSize: '0.9rem' }}>Order snapshot at this moment</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, p: 2, bgcolor: '#13151f', border: '1px solid #2a2d42', borderRadius: 1.5 }}>
                      {selectedEntry.metadata.order_type && (
                        <Box>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order Type</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>{friendlyOrderType(selectedEntry.metadata.order_type)}</Typography>
                        </Box>
                      )}
                      {selectedEntry.metadata.customer_name && (
                        <Box>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer</Typography>
                          <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>{selectedEntry.metadata.customer_name}</Typography>
                        </Box>
                      )}
                      {typeof selectedEntry.metadata.total === 'number' && (
                        <Box>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</Typography>
                          <Typography sx={{ color: '#00C896', fontSize: '0.95rem', fontWeight: 700 }}>€{Number(selectedEntry.metadata.total).toFixed(2)}</Typography>
                        </Box>
                      )}
                      {/* Fallback: any other metadata keys that aren't in the curated list */}
                      {Object.entries(selectedEntry.metadata)
                        .filter(([k]) => !['order_type', 'customer_name', 'total'].includes(k))
                        .map(([k, v]) => (
                          <Box key={k}>
                            <Typography sx={{ color: '#9ca3af', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{friendlyField(k)}</Typography>
                            <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>{friendlyValue(k, v)}</Typography>
                          </Box>
                        ))}
                    </Box>
                  </>
                )}
              </DialogContent>
              <DialogActions sx={{ borderTop: '1px solid #2a2d42', px: 3, py: 2 }}>
                <Typography sx={{ color: '#6b7280', fontSize: '0.68rem', flex: 1 }}>
                  Entry recorded {formatTime(selectedEntry.timestamp)}
                </Typography>
                <Button onClick={() => setSelectedEntry(null)} variant="contained"
                  sx={{ bgcolor: '#00C896', color: '#1a1d2e', fontWeight: 700, '&:hover': { bgcolor: '#7bc012' } }}>
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default AuditLogPage;
